import React, { useState } from "react";
import {
  Button,
  Form,
  Input,
  InputNumber,
  Space,
  Switch,
  message,
  Typography,
} from "antd";
import { MinusCircleOutlined, PlusOutlined } from "@ant-design/icons";
import { useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { Locale } from "../../utils";
import { getUserInfo } from "../../utils/redux/reducers/User";
import ApiManager from "../../utils/api/ApiManager";
import TranslateBtn from "../../components/product/TranslateBtn";

const i18n = Locale.getInstance();
const api = ApiManager.getInstance().groupon;

function mapIncludedItemsFromApi(items) {
  if (!items?.length) {
    return [{ zh_name: "", ug_name: "", quantity: 1 }];
  }
  return items.map((row) => ({
    zh_name: row.zh_name || "",
    ug_name: row.ug_name || "",
    quantity: row.quantity ?? 1,
    original_price:
      row.original_price != null ? row.original_price / 100 : undefined,
  }));
}

function mapIncludedItemsToApi(items) {
  return items.map((row) => {
    const payload = {
      zh_name: (row.zh_name || "").trim(),
      ug_name: (row.ug_name || "").trim(),
      quantity: row.quantity ?? 1,
    };
    if (row.original_price != null && row.original_price !== "") {
      payload.original_price = Math.round(Number(row.original_price) * 100);
    }
    return payload;
  });
}

function DealEdit() {
  const user = useSelector((state) => getUserInfo(state));
  const history = useHistory();
  const location = useLocation();
  const item = location.state?.item;
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm();

  const onFinish = async (values) => {
    if (!user.token) return;
    setLoading(true);
    const payload = {
      ...values,
      status: values.status ? 1 : 0,
      original_price: Math.round(values.original_price * 100),
      price: Math.round(values.price * 100),
      needed_points: Math.max(0, Math.round(Number(values.needed_points) || 0)),
      included_items: mapIncludedItemsToApi(values.included_items || []),
    };
    try {
      const runner = item
        ? api.productUpdate.run({
            token: user.token,
            path: { id: item.id },
            data: payload,
          })
        : api.productAdd.run({ token: user.token, data: payload });
      const res = await runner;
      if (res.result) {
        message.success(res.message || i18n.t("groupon.save"));
        history.push("/main/groupon/deals");
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ padding: 24, maxWidth: 720 }}>
      <Typography.Title level={4} style={{ marginBottom: 24 }}>
        {item ? i18n.t("groupon.editDeal") : i18n.t("groupon.addDeal")}
      </Typography.Title>
      <Form
        form={form}
        labelCol={{ span: 6 }}
        wrapperCol={{ span: 16 }}
        onFinish={onFinish}
        initialValues={{
          status: item ? item.status === 1 : true,
          valid_days: item?.valid_days || 30,
          sort_order: item?.sort_order || 0,
          zh_name: item?.zh_name || "",
          ug_name: item?.ug_name || "",
          zh_description: item?.zh_description || "",
          ug_description: item?.ug_description || "",
          original_price: item ? item.original_price / 100 : 0,
          price: item ? item.price / 100 : 0,
          needed_points: item?.needed_points ?? 0,
          included_items: mapIncludedItemsFromApi(item?.included_items),
        }}
      >
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => (
            <Form.Item
              name="zh_name"
              label={i18n.t("groupon.zhName")}
              rules={[{ required: true }]}
            >
              <Input
                addonAfter={
                  <TranslateBtn
                    context={getFieldValue("zh_name")}
                    onFinish={(value) => setFieldsValue({ ug_name: value })}
                    from="zh"
                    to="ug"
                  />
                }
              />
            </Form.Item>
          )}
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => (
            <Form.Item
              name="ug_name"
              label={i18n.t("groupon.ugName")}
              rules={[{ required: true }]}
            >
              <Input
                dir="rtl"
                addonAfter={
                  <TranslateBtn
                    context={getFieldValue("ug_name")}
                    onFinish={(value) => setFieldsValue({ zh_name: value })}
                    from="ug"
                    to="zh"
                  />
                }
              />
            </Form.Item>
          )}
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => (
            <Form.Item
              name="zh_description"
              label={i18n.t("groupon.zhDescription")}
              extra={
                <TranslateBtn
                  context={getFieldValue("zh_description")}
                  onFinish={(value) => setFieldsValue({ ug_description: value })}
                  from="zh"
                  to="ug"
                  btnType="link"
                />
              }
            >
              <Input.TextArea rows={3} />
            </Form.Item>
          )}
        </Form.Item>
        <Form.Item noStyle shouldUpdate>
          {({ getFieldValue, setFieldsValue }) => (
            <Form.Item
              name="ug_description"
              label={i18n.t("groupon.ugDescription")}
              extra={
                <TranslateBtn
                  context={getFieldValue("ug_description")}
                  onFinish={(value) => setFieldsValue({ zh_description: value })}
                  from="ug"
                  to="zh"
                  btnType="link"
                />
              }
            >
              <Input.TextArea rows={3} dir="rtl" />
            </Form.Item>
          )}
        </Form.Item>
        <Form.Item
          name="original_price"
          label={i18n.t("groupon.originalPrice")}
          rules={[{ required: true }]}
        >
          <InputNumber min={0} step={0.01} addonBefore="¥" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="price"
          label={i18n.t("groupon.salePrice")}
          rules={[{ required: true }]}
        >
          <InputNumber min={0} step={0.01} addonBefore="¥" style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="needed_points"
          label={i18n.t("groupon.neededPoints")}
          rules={[{ required: true, message: i18n.t("groupon.neededPointsRequired") }]}
        >
          <InputNumber min={0} step={1} precision={0} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="valid_days"
          label={i18n.t("groupon.validDays")}
          rules={[{ required: true }]}
        >
          <InputNumber min={1} style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item label={i18n.t("groupon.includedItems")} required>
          <Form.List name="included_items">
            {(fields, { add, remove }) => (
              <>
                {fields.map(({ key, name, ...restField }) => (
                  <div
                    key={key}
                    style={{
                      border: "1px solid #f0f0f0",
                      borderRadius: 4,
                      padding: 12,
                      marginBottom: 12,
                    }}
                  >
                    <Form.Item noStyle shouldUpdate>
                      {({ getFieldValue, setFieldsValue }) => {
                        const updateItemField = (field, value) => {
                          const rows = [...(getFieldValue("included_items") || [])];
                          rows[name] = { ...rows[name], [field]: value };
                          setFieldsValue({ included_items: rows });
                        };
                        return (
                          <>
                            <Form.Item
                              {...restField}
                              name={[name, "zh_name"]}
                              label={i18n.t("groupon.zhName")}
                              rules={[{ required: true }]}
                            >
                              <Input
                                addonAfter={
                                  <TranslateBtn
                                    context={getFieldValue([
                                      "included_items",
                                      name,
                                      "zh_name",
                                    ])}
                                    onFinish={(value) =>
                                      updateItemField("ug_name", value)
                                    }
                                    from="zh"
                                    to="ug"
                                  />
                                }
                              />
                            </Form.Item>
                            <Form.Item
                              {...restField}
                              name={[name, "ug_name"]}
                              label={i18n.t("groupon.ugName")}
                              rules={[{ required: true }]}
                            >
                              <Input
                                dir="rtl"
                                addonAfter={
                                  <TranslateBtn
                                    context={getFieldValue([
                                      "included_items",
                                      name,
                                      "ug_name",
                                    ])}
                                    onFinish={(value) =>
                                      updateItemField("zh_name", value)
                                    }
                                    from="ug"
                                    to="zh"
                                  />
                                }
                              />
                            </Form.Item>
                          </>
                        );
                      }}
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "original_price"]}
                      label={i18n.t("groupon.itemMarketPrice")}
                    >
                      <InputNumber
                        min={0}
                        step={0.01}
                        addonBefore="¥"
                        style={{ width: "100%" }}
                      />
                    </Form.Item>
                    <Form.Item
                      {...restField}
                      name={[name, "quantity"]}
                      label={i18n.t("groupon.itemQuantity")}
                      rules={[{ required: true }]}
                      initialValue={1}
                    >
                      <InputNumber min={1} style={{ width: "100%" }} />
                    </Form.Item>
                    <Button
                      type="link"
                      danger
                      icon={<MinusCircleOutlined />}
                      onClick={() => remove(name)}
                    >
                      {i18n.t("common.delete")}
                    </Button>
                  </div>
                ))}
                <Button
                  type="dashed"
                  onClick={() => add({ zh_name: "", ug_name: "", quantity: 1 })}
                  block
                  icon={<PlusOutlined />}
                >
                  {i18n.t("groupon.addItem")}
                </Button>
              </>
            )}
          </Form.List>
        </Form.Item>
        <Form.Item name="sort_order" label={i18n.t("groupon.sortOrder")}>
          <InputNumber style={{ width: "100%" }} />
        </Form.Item>
        <Form.Item
          name="status"
          label={i18n.t("groupon.active")}
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>
        <Form.Item wrapperCol={{ offset: 6 }}>
          <Space>
            <Button onClick={() => history.goBack()}>
              {i18n.t("groupon.cancel")}
            </Button>
            <Button type="primary" htmlType="submit" loading={loading}>
              {i18n.t("groupon.save")}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </div>
  );
}

export default DealEdit;
